/**
 * Где ролик сейчас — одно на всех.
 *
 * ЗАЧЕМ. До этого у каждой копии ролика была своя жизнь: панель
 * запускала свою при открытии вкладки, оверлей — при создании слоя,
 * а перезапущенный в OBS источник начинал заново. Никто ни с кем
 * не сверялся, и «рассинхрон» был не сбоем, а устройством: сравнивать
 * было не с чем.
 *
 * ТЕПЕРЬ СОСТОЯНИЕ ЖИВЁТ НА СЕРВЕРЕ. Не позиция сама по себе —
 * её пришлось бы слать шестьдесят раз в секунду, — а ОТМЕТКА:
 * «в момент серверного времени T ролик был на позиции P и играл».
 * Дальше каждый клиент считает сам: прошло столько-то, значит
 * позиция такая-то. Ровно так же в системе устроены таймеры сцены,
 * и по той же причине — тикать по сети незачем.
 *
 * ЧАСЫ У ВСЕХ УЖЕ ОБЩИЕ. Сервер называет своё время в `welcome`
 * и в каждом `pong`, клиент держит поправку (widgets/clock). Без неё
 * отметка была бы бесполезна: часы на машине стримера врут
 * на минуты.
 *
 * ЭТО НЕ СЦЕНА. Состояние воспроизведения не хранится в базе,
 * не попадает в отмену и не приезжает в раскладе: сцена говорит
 * «ролик играет по кругу», а отметка — «вот где он прямо сейчас».
 * Смешать их значило бы получить расклад, восстанавливающий эфир
 * на позиции двухдневной давности.
 */
/** Отметка: где был ролик в названный момент серверного времени. */
import { decodeMessage } from './message.js';
/** Предел позиции — сутки. Тот же, что у перемотки. */
const MAX_POSITION_MS = 24 * 60 * 60 * 1000;
/**
 * Разбирает сообщение `playback`.
 *
 * Числа приходят из сети и уходят прямо в проигрыватель, где
 * бесконечность и NaN дают не перемотку, а неопределённое поведение.
 */
export function parsePlayback(raw) {
    const parsed = decodeMessage(raw);
    if (parsed === null)
        return null;
    const message = parsed;
    if (message.t !== 'playback')
        return null;
    if (typeof message.layerId !== 'string' || message.layerId === '')
        return null;
    if (typeof message.playing !== 'boolean')
        return null;
    const position = message.positionMs;
    if (typeof position !== 'number' || !Number.isFinite(position))
        return null;
    if (position < 0 || position > MAX_POSITION_MS)
        return null;
    const at = message.atMs;
    if (typeof at !== 'number' || !Number.isFinite(at) || at <= 0)
        return null;
    return { layerId: message.layerId, playing: message.playing, positionMs: position, atMs: at };
}
/**
 * Куда встать прямо сейчас, мс от начала файла.
 *
 * ПЕТЛЯ УЧИТЫВАЕТСЯ ЗДЕСЬ, а не в проигрывателе: ролик, зациклённый
 * на десять секунд, за минуту уходит на шестую итерацию, и без
 * остатка от деления клиент, подключившийся позже, попытался бы
 * встать на шестидесятой секунде десятисекундного файла.
 *
 * Длина неизвестна (метаданные ещё не пришли) — возвращаем как есть:
 * проигрыватель сам зажмёт значение, когда узнает длину.
 */
export function playbackAt(state, now, options = {}) {
    const elapsed = state.playing ? Math.max(0, now - state.atMs) : 0;
    const raw = state.positionMs + elapsed;
    const duration = options.durationMs ?? 0;
    if (!options.loop || !(duration > 0))
        return raw;
    return raw % duration;
}
//# sourceMappingURL=playback.js.map